import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { CreditCard } from 'lucide-react'
import { Ticket } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { MessageSquare } from 'lucide-react'
import { CircleDollarSign } from 'lucide-react'
import { ArrowLeft } from 'lucide-react'
import { ShoppingBag } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/data/ui'
import { Labelled, NumberField, SelectField, TextField } from '@/components/editor/EditorKit'
import { BackdateField } from '@/components/payments/BackdateField'
import { backdateField, backdateProblem } from '@/lib/backdating'
import { todayInZone } from '@/lib/calendar'
import {
  describeDelivery,
  getPaymentOptions,
  recordPayment,
  requestPaymentLink,
  settleBooking,
  type PaymentOptions,
  type SettlePackageOption,
} from '@/lib/api'
import { SaleForm, type SoldSale } from '@/components/sales/SaleForm'
import { PAYMENT_METHODS, type PaymentMethodValue } from '@/lib/paymentMethods'
import { documentToastLine } from '@/lib/documents'
import { buildSettleHandoff } from '@/lib/settleHandoff'
import { useAssistantPanel } from '@/components/assistant/AssistantPanelProvider'
import { fmtMoney, fmtDate } from '@/lib/format'
import { creditIneligibleWhy, creditCostSuffix } from '@/lib/creditReasons'
import { cn } from '@/lib/utils'

const GATEWAY_NAME: Record<string, string> = { stripe: 'Stripe', worldline: 'ANZ Worldline' }

// ─── "How is this being paid for?" ─────────────────────────────────────────────
//
// EVERY PAYMENT IS A SALE (operator, 2026-10-17), charged one of two ways, and the
// dialog is those two sections: CHARGE TO A PACKAGE — a credit bought earlier, or
// a package / services sold right here (the same SaleForm as /new-sale) and spent
// on this session in the same motion — or CHARGE TO A PAYMENT METHOD, which is the
// session's own sale: a card link, or money taken now.
//
// The operator asked for this explicitly (2026-08-06): marking a session paid
// must offer the choice between taking a card payment and spending a package
// credit, rather than silently doing one of them.
//
// It is a DELIBERATE widening of the calendar quick-action rule, which until now
// said every action must be a single verb with no arguments. There are two named
// decisions that never take a value — spend a package credit, or send a card
// payment link — and one that does:
//
//   - The credit and card-link options are still whole decisions, never a field.
//     The operator picks WHICH named thing happens; they never type a value that
//     could be wrong. Both are enumerated by the server (`/payment-options`), so
//     the dialog cannot offer something the endpoint would then refuse — a credit
//     it shows is a credit /settle will accept.
//   - "Record a payment taken now" DOES type an amount and pick a method (cash,
//     bank transfer, or the card gateway). Added 2026-09-19 at the operator's
//     request: a session can be paid off with any of the existing payment
//     methods, not only against a package. This is the same exception already
//     granted to RecordPaymentDialog — a part payment has no set of named options
//     to choose from, and the figure comes off a receipt or a bank statement.
//     What keeps the typed field safe is that the SERVER owns every limit: it
//     refuses more than the balance due (unless the price is overridden), refuses
//     anything at all on a package-settled session, and mints the receipt. The
//     amount defaults to the full balance and is only editable DOWN, for a part
//     payment. Nothing is charged here — this records money taken outside the app.
//   - Anything else (a refund, a price correction, cancelling a live link) still
//     goes to chat.
//
// The tenant's `default_payment_method` decides which of the credit/card options
// leads. The dialog shows `recommended_method`, not the raw setting: a business
// set to lead with a payment link but with no gateway configured must not be
// shown a card button that can only fail.

export interface PaymentDialogProps {
  bookingId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Refetch whatever is behind the dialog after money (or a credit) moves. */
  onChanged?: () => void
}

type Busy =
  | { kind: 'card' }
  | { kind: 'credit'; id: string }
  | { kind: 'apply' }
  | { kind: 'record' }
  | null

function errorDetail(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e)
  try {
    const parsed = JSON.parse(raw) as { error?: { detail?: string; message?: string; reason?: string } }
    return parsed.error?.detail ?? parsed.error?.message ?? parsed.error?.reason ?? raw
  } catch {
    return raw
  }
}

const CREDIT_UNAVAILABLE: Record<string, string> = {
  no_packages: 'They hold no packages, so there is no credit to spend.',
  no_eligible_package: 'None of their packages covers this service.',
  partially_paid: 'This session is part-paid. A credit buys a whole session, not a remainder.',
}

const NOT_PAYABLE: Record<string, string> = {
  already_paid: 'This session is already paid in full.',
  package_debited: 'This session was settled with a package credit.',
  nothing_owed: 'This session has no price, so there is nothing to collect.',
  not_chargeable: 'This booking was cancelled or superseded, so there is nothing to collect.',
}

function creditSubtitle(p: SettlePackageOption): string {
  // Booking-scoped by construction: on a per-service package the counts are the
  // booked SERVICE's own allowance, so the label says so.
  const left = `${p.sessions_remaining} of ${p.sessions_total}${p.per_service ? ' for this service' : ''} left`
  const cost = creditCostSuffix(p)
  return p.expires_at
    ? `${left}${cost} · expires ${fmtDate(p.expires_at)}`
    : `${left}${cost} · no expiry`
}

/** One tappable decision. Never a field — see the note at the top of this file. */
function OptionButton({
  icon,
  title,
  subtitle,
  recommended,
  busy,
  disabled,
  onClick,
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  recommended?: boolean
  busy?: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        recommended
          ? 'border-primary bg-primary/5 hover:bg-primary/10'
          : 'border-border bg-card hover:bg-muted',
      )}
    >
      <span className="shrink-0 text-muted-foreground">
        {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[13.5px] font-semibold text-foreground">{title}</span>
          {recommended ? <Badge label="Default" tone="info" /> : null}
        </span>
        <span className="mt-px block truncate text-[11.5px] text-muted-foreground">{subtitle}</span>
      </span>
    </button>
  )
}

/** One of the two ways a session's sale is charged. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">{title}</span>
      {children}
    </div>
  )
}

export function PaymentDialog({ bookingId, open, onOpenChange, onChanged }: PaymentDialogProps) {
  const assistant = useAssistantPanel()
  const [options, setOptions] = useState<PaymentOptions | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState<Busy>(null)

  // "Record a payment taken now" — amount (defaults to the full balance, editable
  // DOWN for a part payment), method, and an optional reference. The server owns
  // every limit; the client checks the same rules only so the operator hears
  // about it before pressing the button.
  const [payAmount, setPayAmount] = useState('')
  const [payMethod, setPayMethod] = useState<PaymentMethodValue>('payment_gateway')
  const [payReference, setPayReference] = useState('')
  // Empty until the options say which zone "today" is in; then today.
  const [payOn, setPayOn] = useState('')

  // Refetched every time the dialog opens rather than cached: a credit may have
  // been spent, or a link paid, since the last look. Deciding how to take money
  // off stale state is exactly how a session gets charged for twice.
  useEffect(() => {
    if (!open || !bookingId) return
    let live = true
    setLoading(true)
    setLoadError(null)
    setOptions(null)
    getPaymentOptions(bookingId)
      .then((res) => {
        if (!live) return
        setOptions(res)
        // Prefill the amount to the whole balance, which is what is paid most of
        // the time. It stays editable because a part payment is the reason the
        // typed field exists.
        setPayAmount(res.payable ? String(res.balance_due) : '')
        setPayOn(todayInZone(res.timezone))
      })
      .catch((e) => { if (live) setLoadError(errorDetail(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [open, bookingId])

  // "Sell it now and use it": the SAME SaleForm as /new-sale, with the buyer fixed.
  // The sale is made first, then this session is paid from it.
  const [view, setView] = useState<'options' | 'sell'>('options')

  useEffect(() => {
    if (!open) {
      setView('options')
      // Never carry a method or a reference into a second use — and the amount is
      // re-prefilled from fresh options on the next open, never inherited.
      setPayMethod('payment_gateway'); setPayReference(''); setPayAmount(''); setPayOn('')
    }
  }, [open])

  const close = useCallback(() => { setBusy(null); onOpenChange(false) }, [onOpenChange])

  async function applySold(sold: SoldSale) {
    if (!bookingId || !sold.sale) return
    setBusy({ kind: 'apply' })
    try {
      const result = await settleBooking(bookingId, sold.sale.id)
      if (result.settled) {
        toast.success(`${sold.heading} — this session is paid from it`, { description: sold.money })
      } else {
        // The sale stands; only this session could not be paid from it.
        toast.info(`${sold.heading}, but it could not pay for this session`, {
          description: [result.message, sold.money].filter(Boolean).join(' '),
        })
      }
    } catch (e) {
      toast.error(`${sold.heading}, but this session could not be paid from it`, { description: errorDetail(e) })
    }
    onChanged?.()
    close()
  }

  async function spendCredit(customerPackageId: string) {
    if (!bookingId || busy) return
    setBusy({ kind: 'credit', id: customerPackageId })
    try {
      const result = await settleBooking(bookingId, customerPackageId)
      if (result.settled) {
        toast.success(`Paid with ${result.package_name}`, {
          description: `${result.sessions_remaining} of ${result.sessions_total} sessions left.`,
        })
        onChanged?.()
        close()
        return
      }
      // The endpoint refused a credit this dialog offered. That means the picture
      // moved under us (a concurrent spend, an expiry crossing midnight), so hand
      // the whole thing to chat rather than guessing again. The assistant panel
      // slides out in place — the operator stays on the screen they were on.
      toast.info("That credit could not be spent", { description: result.message })
      assistant.ask(buildSettleHandoff(result))
      close()
    } catch (e) {
      toast.error("Couldn't spend the credit", { description: errorDetail(e) })
      setBusy(null)
    }
  }

  async function sendLink() {
    if (!bookingId || busy) return
    setBusy({ kind: 'card' })
    try {
      const result = await requestPaymentLink(bookingId)
      const amount = fmtMoney(Number(result.payment_link.amount), result.payment_link.currency)
      const where = describeDelivery(result)
      if (where) {
        toast.success(
          result.created ? `Payment link sent — ${amount}` : `Existing link re-sent — ${amount}`,
          { description: where },
        )
      } else {
        // The link exists and is valid; only delivery failed. Asking again re-sends
        // the same one, so say that rather than implying nothing happened.
        toast.warning(`Payment link ready — ${amount}`, {
          description: result.email_error ?? 'It could not be delivered. Open the booking to copy the link.',
        })
      }
      onChanged?.()
      close()
    } catch (e) {
      toast.error("Couldn't request payment", { description: errorDetail(e) })
      setBusy(null)
    }
  }

  async function recordDirectPayment() {
    if (!bookingId || !options || busy) return
    const amount = Number(payAmount)
    setBusy({ kind: 'record' })
    try {
      // Every payment mints and emails its own receipt server-side — the toast
      // says what the document did so a delivery problem is heard about here.
      const result = await recordPayment(bookingId, {
        amount,
        payment_method: payMethod,
        reference: payReference.trim() || null,
        ...backdateField('paid_at', payOn, payToday),
      })
      toast.success(`${fmtMoney(amount, options.currency)} recorded`, {
        description: documentToastLine(result.document),
      })
      onChanged?.()
      close()
    } catch (e) {
      toast.error("Couldn't record the payment", { description: errorDetail(e) })
      setBusy(null)
    }
  }

  function askInChat() {
    if (!bookingId) return
    // The operator reads names; the ids the model needs ride in a trailing
    // [Screen context: …] line, which the panel's message renderer strips for
    // display — same convention as every other panel handoff.
    const question = options
      ? `How should I take payment for the ${options.service_name ?? 'session'} for ${options.customer_name ?? 'this customer'}? ${options.currency} ${options.balance_due.toFixed(2)} outstanding.\n\n[Screen context: taking payment for a booking (booking_id: ${bookingId}, customer_id: ${options.customer_id}) — use these ids directly]`
      : `How should I take payment for this session?\n\n[Screen context: taking payment for a booking (booking_id: ${bookingId}) — use this id directly]`
    close()
    assistant.ask(question)
  }

  const credit = options?.package_credit
  const card = options?.card
  /** Spent/expired packages the server withheld from the ineligible list. */
  const omittedCredits = credit?.ineligible_omitted ?? 0
  const cardLeads = options?.recommended_method === 'payment_link'
  const gatewayName = card?.gateway ? GATEWAY_NAME[card.gateway] : null
  const channelText = card?.channels?.length
    ? card.channels.map((c) => (c === 'in_app' ? 'the app' : c === 'sms' ? 'text' : 'email')).join(', ')
    : 'no channel available'

  // "Record a payment taken now" validation — a client mirror of the server's
  // limit, so the operator hears about it before pressing the button. The server
  // is the authority: an amount over the balance is refused unless the price was
  // deliberately overridden.
  const payNum = Number(payAmount)
  const payToday = todayInZone(options?.timezone)
  const payDateProblem = payOn ? backdateProblem(payOn, payToday) : null
  const payMethodHint = PAYMENT_METHODS.find((m) => m.value === payMethod)?.hint
  const payAmountOk = Number.isFinite(payNum) && payNum > 0
  const payOverBalance =
    payAmountOk && !!options && !options.price_overridden && payNum > options.balance_due
  const payBlocker: string | null =
    !payAmount || !options
      ? null
      : !payAmountOk
        ? 'Enter an amount greater than zero.'
        : payOverBalance
          ? `That is more than the ${fmtMoney(options.balance_due, options.currency)} outstanding.`
          : payDateProblem
  const canRecord = payAmountOk && !payOverBalance && !payDateProblem

  const cardOption = card ? (
    card.available ? (
      <div className="flex flex-col gap-1">
        <OptionButton
          icon={<CreditCard className="size-4" aria-hidden />}
          title={`${card.open_link ? 'Re-send the payment link' : 'Send a payment link'}${gatewayName ? ` · ${gatewayName}` : ''}`}
          subtitle={
            card.open_link
              ? `A link is already open — this re-sends the same one, by ${channelText}`
              : `${fmtMoney(options!.balance_due, options!.currency)} by ${channelText}`
          }
          recommended={cardLeads}
          busy={busy?.kind === 'card'}
          disabled={busy !== null}
          onClick={() => void sendLink()}
        />
        {card.test_mode ? (
          <p className="px-1 text-[12px] leading-[16px] text-amber-700">
            {gatewayName ?? 'The card gateway'} is in test mode: the link takes test cards only, so a customer cannot pay it with a real card.
          </p>
        ) : null}
      </div>
    ) : (
      <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12px] text-muted-foreground">
        Card payments are not set up for this business, so a payment link cannot be sent.
      </p>
    )
  ) : null

  const sellEntry = (
    <button
      type="button"
      onClick={() => setView('sell')}
      disabled={busy !== null}
      className="flex items-center gap-1.5 self-start px-0.5 py-1 text-[12.5px] font-semibold text-blue-700 disabled:opacity-45"
    >
      <ShoppingBag className="size-3.5" aria-hidden />
      Sell a package or services and use it now…
    </button>
  )

  const creditOption = credit ? (
    credit.available ? (
      <div className="flex flex-col gap-1.5">
        {credit.ambiguous ? (
          <p className="text-[11.5px] text-muted-foreground">
            More than one package could cover this — pick which credit to spend.
          </p>
        ) : null}
        {credit.eligible.map((p) => (
          <OptionButton
            key={p.customer_package_id}
            icon={<Ticket className="size-4" aria-hidden />}
            title={p.name}
            subtitle={creditSubtitle(p)}
            // Only mark a credit as the default when it is the ONE that would be
            // spent anyway. With several to choose between there is no default —
            // badging one would nudge the operator into a money decision the
            // server deliberately refuses to make for them.
            recommended={
              !cardLeads
              && !credit.ambiguous
              && p.customer_package_id === credit.auto_spendable_customer_package_id
            }
            busy={busy?.kind === 'credit' && busy.id === p.customer_package_id}
            disabled={busy !== null}
            onClick={() => void spendCredit(p.customer_package_id)}
          />
        ))}
      </div>
    ) : (
      <p className="rounded-lg border border-dashed border-border px-3 py-2.5 text-[12px] text-muted-foreground">
        {CREDIT_UNAVAILABLE[credit.unavailable_reason ?? ''] ?? 'No package credit can pay for this session.'}
      </p>
    )
  ) : null

  // Pay the session off directly by any of the existing methods. The typed
  // amount is the granted exception (see the note at the top of this file): it
  // defaults to the full balance and is editable DOWN for a part payment, and
  // the server owns every limit.
  const recordOption = options ? (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3">
      <span className="text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
        Or record a payment taken now
      </span>
      <Labelled label="Amount" required>
        <NumberField
          value={payAmount}
          onChange={setPayAmount}
          min={0}
          step={1}
          prefix={options.currency}
          disabled={busy !== null}
        />
      </Labelled>
      <Labelled label="How it was taken" required hint={payMethodHint}>
        <SelectField
          value={payMethod}
          onChange={setPayMethod}
          disabled={busy !== null}
          options={PAYMENT_METHODS.map((m) => ({ value: m.value, label: m.label }))}
        />
      </Labelled>
      <Labelled
        label="Reference"
        hint="Receipt or transaction number from wherever it was taken. Optional."
      >
        <TextField value={payReference} onChange={setPayReference} disabled={busy !== null} />
      </Labelled>
      <BackdateField
        label="Date paid"
        value={payOn}
        onChange={setPayOn}
        today={payToday}
        disabled={busy !== null}
        backdatedHint={(d) => `Recorded as taken on ${d}. The receipt is dated today and says the money was received on ${d}.`}
      />
      {payBlocker ? <p className="text-[11.5px] text-rose-700">{payBlocker}</p> : null}
      <button
        type="button"
        disabled={busy !== null || !canRecord}
        onClick={() => void recordDirectPayment()}
        className={cn(
          'inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-2 text-[12.5px] font-semibold',
          'bg-foreground text-background transition-opacity hover:opacity-90',
          'disabled:cursor-not-allowed disabled:opacity-45',
        )}
      >
        {busy?.kind === 'record'
          ? <Loader2 className="size-3.5 animate-spin" aria-hidden />
          : <CircleDollarSign className="size-3.5" aria-hidden />}
        Record payment
      </button>
      <p className="text-[11px] text-muted-foreground">
        Records money already taken (cash, bank transfer, or the card gateway) — nothing is charged from here.
      </p>
    </div>
  ) : null

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {options?.payable === false ? 'Nothing to collect' : 'How is this being paid?'}
          </DialogTitle>
          <DialogDescription>
            {options
              ? options.payable
                ? `${options.service_name ?? 'Session'}${options.customer_name ? ` · ${options.customer_name}` : ''} · ${fmtMoney(options.balance_due, options.currency)} outstanding`
                : (NOT_PAYABLE[options.not_payable_reason ?? ''] ?? 'This session has nothing outstanding.')
              : 'Loading the payment picture…'}
          </DialogDescription>
        </DialogHeader>

        {view === 'sell' && options ? (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setView('options')}
              disabled={busy !== null}
              className="flex items-center gap-1.5 self-start px-0.5 py-1 text-[12.5px] font-semibold text-blue-700 disabled:opacity-45"
            >
              <ArrowLeft className="size-3.5" aria-hidden />
              Back to the payment options
            </button>
            {busy?.kind === 'apply' ? (
              <div className="flex items-center gap-2 py-6 text-[12.5px] text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Paying this session from the sale…
              </div>
            ) : (
              <SaleForm
                customer={{ id: options.customer_id, name: options.customer_name ?? 'This customer', phone: null }}
                presetService={options.service_id
                  ? { id: options.service_id, name: options.service_name ?? 'This treatment', price: options.price }
                  : undefined}
                coversServiceId={options.service_id}
                // A membership grants nothing until its first payment, so it cannot pay for today.
                allowMembership={false}
                submitLabel="Sell and use it now"
                onSold={(sold) => void applySold(sold)}
              />
            )}
          </div>
        ) : loading ? (
          <div className="flex items-center gap-2 py-6 text-[12.5px] text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Working out how this can be paid…
          </div>
        ) : loadError ? (
          <p className="py-4 text-[12.5px] text-destructive">{loadError}</p>
        ) : options?.payable ? (
          <div className="flex flex-col gap-3">
            {/* The recommended method leads. Order carries the recommendation as
                much as the badge does, so a rushed operator picking the top
                option picks the configured default. */}
            {cardLeads ? (
              <>
                <Section title="Charge to a payment method">
                  {cardOption}
                  {recordOption}
                </Section>
                <Section title="Or charge to a package">
                  {creditOption}
                  {sellEntry}
                </Section>
              </>
            ) : (
              <>
                <Section title="Charge to a package">
                  {creditOption}
                  {sellEntry}
                </Section>
                <Section title="Or charge to a payment method">
                  {cardOption}
                  {recordOption}
                </Section>
              </>
            )}

            {/* Only packages that could still pay for something are listed —
                the server withholds spent-and-expired ones and counts them in
                ineligible_omitted, so the list stays honest without burying the
                usable credit under a customer's whole purchase history. */}
            {credit?.ineligible?.length || omittedCredits > 0 ? (
              <div className="rounded-lg bg-muted px-3 py-2">
                <span className="block text-[11px] font-semibold tracking-wide text-muted-foreground uppercase">
                  Not usable here
                </span>
                {(credit?.ineligible ?? []).map((p) => (
                  <span key={p.customer_package_id} className="mt-0.5 block truncate text-[11.5px] text-muted-foreground">
                    {p.name} — {creditIneligibleWhy(p)}
                    {p.ineligible_reason === 'expired' && (p.package_sessions_remaining ?? 0) > 0
                      ? ` (${p.package_sessions_remaining} unused)`
                      : ''}
                  </span>
                ))}
                {omittedCredits > 0 ? (
                  <span className="mt-0.5 block text-[11.5px] text-muted-foreground/70">
                    {omittedCredits} spent or expired {omittedCredits === 1 ? 'package' : 'packages'} not shown
                  </span>
                ) : null}
              </div>
            ) : null}

            {/* The escape hatch that keeps this dialog small. Anything the three
                options above do not cover — a refund, a price correction,
                cancelling a live link — belongs in chat. */}
            <button
              type="button"
              onClick={askInChat}
              className="flex items-center gap-1.5 self-start px-0.5 py-1 text-[12.5px] font-semibold text-blue-700"
            >
              <MessageSquare className="size-3.5" aria-hidden />
              Something else — ask in chat
            </button>
          </div>
        ) : options?.card?.open_link ? (
          // Not payable, but a link is still live at the gateway. Somebody should
          // know, or the customer can pay for a session that is already settled.
          <p className="py-1 text-[12.5px] text-muted-foreground">
            A payment link for {fmtMoney(options.card.open_link.amount, options.card.open_link.currency)} is still
            open for this session. Ask in chat to cancel it.
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
